/**
 * Order Log — header controls (ORD-1.3 / ORD-1.4)
 * Columns picker and the Paid/Cancelled status segments. Kept together because
 * both sit in the Order Log panel header.
 */
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { T } from '../../reportTokens';
import type { OrderLogColumnDef, OrderLogStatus } from './orderLogModel';
import { columnCountLabel, OPERATIONS_PRESET, FINANCIAL_PRESET } from './orderLogModel';

// ─── Columns picker ──────────────────────────────────────────────────────────

export interface OrderLogColumnsMenuProps {
    columns: OrderLogColumnDef[];
    visible: string[];
    onChange: (next: string[]) => void;
}

const GROUP_LABEL: Record<string, string> = { operations: 'Order Details', financial: 'Financial Breakdown' };

const MENU_W = 260;
const MENU_MAX_H = 380;
const MENU_MIN_H = 180;   // below this the list is useless — flip instead of squashing
const MENU_GAP = 6;       // between the button and the panel
const VIEWPORT_PAD = 12;  // never touch the window edge

interface MenuPos { top: number; left: number; maxHeight: number }

/**
 * Place the panel against the VIEWPORT, not the card.
 *
 * It used to be `position:absolute` inside the Order Log card, so a log with
 * only a row or two left the panel hanging below the end of the page — clipped,
 * with nothing left to scroll to reach the last checkboxes. Fixed positioning
 * off the button's rect lets it flip above the button when the space below is
 * too short, and caps its height to whatever room is actually left.
 */
function placeMenu(btn: HTMLElement): MenuPos {
    const r = btn.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - MENU_GAP - VIEWPORT_PAD;
    const above = r.top - MENU_GAP - VIEWPORT_PAD;
    // Prefer opening downwards; flip up only when that genuinely buys more room.
    const openUp = below < MENU_MIN_H && above > below;
    const room = openUp ? above : below;
    const maxHeight = Math.max(MENU_MIN_H, Math.min(MENU_MAX_H, room));
    return {
        top: openUp ? Math.max(VIEWPORT_PAD, r.top - MENU_GAP - maxHeight) : r.bottom + MENU_GAP,
        // Right-aligned with the button, clamped so it can't run off either edge.
        left: Math.max(VIEWPORT_PAD, Math.min(r.right - MENU_W, window.innerWidth - MENU_W - VIEWPORT_PAD)),
        maxHeight,
    };
}

export function OrderLogColumnsMenu({ columns, visible, onChange }: OrderLogColumnsMenuProps) {
    const [open, setOpen] = useState(false);
    const btnRef = useRef<HTMLButtonElement>(null);
    const [pos, setPos] = useState<MenuPos | null>(null);

    const reposition = useCallback(() => {
        if (btnRef.current) setPos(placeMenu(btnRef.current));
    }, []);

    // Measured before paint so the panel never flashes in the wrong place. The
    // scroll listener is capturing so it also fires for the drill's inner
    // scroll containers, not just the window.
    useLayoutEffect(() => {
        if (!open) { setPos(null); return; }
        reposition();
        window.addEventListener('resize', reposition);
        window.addEventListener('scroll', reposition, true);
        return () => {
            window.removeEventListener('resize', reposition);
            window.removeEventListener('scroll', reposition, true);
        };
    }, [open, reposition]);

    const toggle = (key: string) => {
        onChange(visible.indexOf(key) >= 0 ? visible.filter(k => k !== key) : [...visible, key]);
    };
    const applyPreset = (preset: string[]) => {
        const keys = columns.filter(c => c.locked || c.pinned || preset.indexOf(c.key) >= 0).map(c => c.key);
        onChange(keys);
    };

    const groups: OrderLogColumnGroupRender[] = [];
    columns.forEach(col => {
        const last = groups[groups.length - 1];
        if (!last || last.group !== col.group) groups.push({ group: col.group, cols: [col] });
        else last.cols.push(col);
    });

    return (
        <div style={{ position: 'relative' }}>
            <button
                ref={btnRef}
                onClick={() => setOpen(v => !v)}
                style={{ height: 32, padding: '0 10px', border: `1px solid ${T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t2, background: T.surface, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontFamily: T.font }}
            >
                <span className="material-symbols-rounded" style={{ fontSize: 15 }}>view_week</span>
                Columns ({columnCountLabel(columns, visible)})
            </button>
            {/* Portalled to <body> so no ancestor's overflow or stacking context can
                clip it, and positioned against the viewport by placeMenu above. */}
            {open && pos && createPortal(
                <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 299 }} onClick={() => setOpen(false)} />
                <div style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 300, width: MENU_W, maxHeight: pos.maxHeight, overflowY: 'auto', boxSizing: 'border-box', background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', padding: 10 }}>
                    {/* Presets */}
                    <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                        {[{ label: 'Operations', preset: OPERATIONS_PRESET }, { label: 'Financial', preset: FINANCIAL_PRESET }].map(p => (
                            <button
                                key={p.label}
                                onClick={() => applyPreset(p.preset)}
                                style={{ flex: 1, height: 30, borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: T.surface, color: T.t1, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: T.font }}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    {groups.map(({ group, cols }) => (
                        <div key={group}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.5, margin: '10px 0 4px' }}>{GROUP_LABEL[group]}</div>
                            {cols.map(col => {
                                const locked = !!(col.locked || col.pinned);
                                const on = locked || visible.indexOf(col.key) >= 0;
                                return (
                                    <div
                                        key={col.key}
                                        onClick={() => !locked && toggle(col.key)}
                                        style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 4px', fontSize: 12.5, color: locked ? T.t3 : T.t1, cursor: locked ? 'default' : 'pointer', borderRadius: 5 }}
                                    >
                                        <span style={{ width: 15, height: 15, borderRadius: 4, flexShrink: 0, border: `1.5px solid ${on ? T.accent : T.t3}`, background: on ? T.accent : 'transparent', opacity: locked ? 0.55 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {on && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}
                                        </span>
                                        <span style={{ flex: 1 }}>{col.label}</span>
                                        {locked && <span className="material-symbols-rounded" title="Always shown" style={{ fontSize: 13, color: T.t3 }}>lock</span>}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
                </>,
                document.body,
            )}
        </div>
    );
}

interface OrderLogColumnGroupRender { group: string; cols: OrderLogColumnDef[] }

// ─── Paid | Unpaid / Cancelled segments ──────────────────────────────────────

/** The Unpaid log reuses this control with its own primary segment (ORD-1.3). */
export type OrderLogSegmentKey = OrderLogStatus | 'unpaid';

export interface OrderLogStatusSegmentsProps<K extends string = OrderLogStatus> {
    value: K;
    onChange: (next: K) => void;
    /** Count shown on the first segment (Paid, or Unpaid when `primaryKey` says so). */
    paidCount: number;
    cancelledCount: number;
    /** First segment's key + label; defaults to the Paid log's own. */
    primaryKey?: K;
    primaryLabel?: string;
    /** full-width pill row on mobile */
    fullWidth?: boolean;
}

export function OrderLogStatusSegments<K extends string = OrderLogStatus>({ value, onChange, paidCount, cancelledCount, primaryKey, primaryLabel, fullWidth = false }: OrderLogStatusSegmentsProps<K>) {
    const segments: { key: K; label: string }[] = [
        { key: (primaryKey ?? 'paid') as K, label: `${primaryLabel ?? 'Paid'} (${paidCount})` },
        { key: 'cancelled' as K, label: `Cancelled (${cancelledCount})` },
    ];
    return (
        <div style={{ display: fullWidth ? 'flex' : 'inline-flex', width: fullWidth ? '100%' : undefined, gap: 4, padding: 3, background: T.hover, borderRadius: 9, marginBottom: 12 }}>
            {segments.map(s => {
                const active = value === s.key;
                return (
                    <button
                        key={s.key}
                        onClick={() => onChange(s.key)}
                        style={{
                            flex: fullWidth ? 1 : undefined, height: 30, padding: '0 16px', borderRadius: 7, border: 'none',
                            background: active ? T.surface : 'transparent', color: active ? T.t1 : T.t2,
                            fontWeight: active ? 600 : 500, fontSize: 12.5, cursor: 'pointer', fontFamily: T.font,
                            boxShadow: active ? T.shadow : 'none', whiteSpace: 'nowrap',
                        }}
                    >
                        {s.label}
                    </button>
                );
            })}
        </div>
    );
}

/** Sits directly under the status control whenever the status is not Paid. */
export function OrderLogStatusHelper({ text }: { text: string }) {
    return <div style={{ fontSize: 12, color: T.inkMid, marginTop: -6, marginBottom: 12 }}>{text}</div>;
}
